// TASK-FILES: the twin's seeded task files (the API's demo seed attaches the same kinds): a PDF
// brief, a pipeline chart (PNG), a candidates CSV and, uploaded by Riley, a shortlist in Markdown.
// The chart is a real 480×270 PNG, so the preview and its thumbnail show something.

/** A one-page PDF whose text layer says `text` (hand-built, like the API harness's). */
export function tinyPdf(text: string): Uint8Array {
  const stream = `BT /F1 18 Tf 72 720 Td (${text.replace(/[()\\]/g, "")}) Tj ET`;
  const objs = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    "<< /Type /Pages /Kids [3 0 R] /Count 1 >>",
    "<< /Type /Page /Parent 2 0 R /MediaBox [0 0 612 792] /Contents 4 0 R /Resources << /Font << /F1 5 0 R >> >> >>",
    `<< /Length ${stream.length} >>\nstream\n${stream}\nendstream`,
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
  ];
  let out = "%PDF-1.4\n";
  const offsets: number[] = [];
  objs.forEach((o, i) => {
    offsets.push(out.length);
    out += `${i + 1} 0 obj\n${o}\nendobj\n`;
  });
  const xref = out.length;
  out += `xref\n0 ${objs.length + 1}\n0000000000 65535 f \n`;
  for (const off of offsets) out += `${String(off).padStart(10, "0")} 00000 n \n`;
  out += `trailer\n<< /Size ${objs.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF\n`;
  return new TextEncoder().encode(out);
}

const CHART_PNG_BASE64 = [
  "iVBORw0KGgoAAAANSUhEUgAAAeAAAAEOCAMAAABmRDRVAAADAFBMVEX29/v19vrz9Pnn6e0QuYEfKTc3QVFjZvGLXPYOpenq",
  "6/CRl6DY2+Hv8PWgpK3Dxs7s7vLy8/fu7/Tp6u51fIVqcn4+R1OLkJnLztTBxMtSWWXQ0djV1t1UXGfw8vZ/hpEmMD3l5+v0",
  "9fkhKzlKU2MRGCfP0tmXnaZfZ3XZ3OFlbHonMD4lLjzW2N9OV2Xr7fFnbXlnb3zx8vbT1tzGydDg4udGT19VXWmhpq/KzdSS",
  "l6KZnqe1uMBDTV3b3eNbY3FlbHczPEpBS1uus7mGi5WFi5TKzdPO0ddiaXYxOki/wsnMz9aWm6Q9R1ZXXmrj5eo7RFRudoIj",
  "LDpeZnNhaXTW2d85QlLCxsxsc4DV196xtb2rr7jY2uGOk52mq7JSW2lsb/J8govk5uvy8/ggKjg4QU/n6O0bIjDf4eaQlp+e",
  "oqycoqtweIXh4+egpKw8RVFsdIBGT1uDiJK4vMOiqLHS1NvIy9Gan6i9wcfe4OU7Q0+0uL+7vsXc3uTe4OVASlmTmaOIjpnd",
  "3+SssbpMVWWlqbK5vsW3u8LR1NpCSlZzeYNaYm0qNEEuN0UtNkRDS1dPV2PJzNLm6OzX2eByeoaDipXi5Omqrra8wMekqK9X",
  "X218g49RWWdudH8rNUJwd4FMU19qcX0tM0FTWmYcvYgaqup0e4c/SFg8RlZJUmGKkZt2fYqBhpApMkB9g4yIjZd3fYd7gY2o",
  "rLWCiZM5Q1NhZ3NYYW5cZG9ka3YwOUeMkpzq6u+2usFdzqrd8O2vj/hbwO+UlvXd7vnm5/nq5vrEyM9IUFxASVUsNUSEiZI/",
  "SFShpa4eJTRZYGtKUl6hp7B4f4tASVmepK5DTFw3P0xFTVmAho9mbXqboKqusruSZvcfvYqRZPZpb3orNkN5f4mssLewtLw0",
  "OkdscXtPVmKtsrt3fouRZfYZIC9NVWF+hI6VmqSmqrQ+RVGMkp2yt7+sjPjS6vnf4frS7ekeq+sdquqRk/RYzKjl3/tXvu9F",
  "T1pbX2vh4+gXHSyTZ/dGT16DN7EIAAAACXBIWXMAAAsTAAALEwEAmpwYAAASFUlEQVR42u2dd1xUV9rHn4Gcm0QHgRnUQREN",
  "IBBAIEjTCChEEVARCGiwsDJosJeAvh8LKlHALmqEWBJNUew9ljGbsptmjIlGo9lN2dRNMcn23exm37zPucMMo8IbxAQG5vf9",
  "g7lz7rmXmfO9p975PJcIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAHpqKDSuy89IbzGJOSXBt3tvkdOrSrJ9mzffv1DR4z",
  "okMHZ3Jp377q5/5qqUm5Nya66fU6dUOXXKFzBMH3ilp8DxobyuMmxP2NO9tgIbT1JN8nRL8Gj/EQois5C9Ht5/5qXUSPG9JM",
  "A4TozK/u+0OEKFiR7UCChTjlAILHCbNgD/NXHpDvCILv02q1keyg0y8mWKfXu///gjV6fVgzCE7z7aEKLvEVXbLjVwgx0REE",
  "D5GvmpXiValiUcz0ZcdfV3snr+VrOnU7tc4ieN2HHh6DOPXxTinbTskMQzz26wbOXNY/UnU45tz0YwYbwePC9+rfefnSwRLe",
  "rgoPN1C78PDY9VvXbNvtJneXfrgsZeZAvUVwWHg4l3uuh8fGSV92WpGk4XSfom4D5p74n3o+86Tw8BFEReHhnnxIeDhnMWyf",
  "O2BtQITcme1xJuVc5zCLYM0pD4+xlgN91ooDquB+QvBRkUI87jCC/QtEFyKnteamaxuX1fxgdTNFaxbs/aQQHhpL6hpnomFi",
  "5WC1955P5N1FbgUvqxPcSbyinuxkRW0TnS/E8RSZ8jKPwwatUk9zsfSaJnogl7iaPoZl9Fe3Ctrf+JmXCjGLKEaIvUThQnhR",
  "Vaaady1fdqn71M1O+bWCWei/9ZYDY8XxSFXw+qSkdqrmzo4g+Mwbb7zxaaZYzSPlKnZbNOkVIeZTu5NC9N/9byHeVwVr3hfi",
  "hBOZVonM/Ve4bLdKwSL4nYmc+TjRKSH2bX1cyq8TLDL7b2Wj2+oEiw8e78+Dm3HkPl0ULJ98TIg3rhdcsHURn/2khgKEWBbQ",
  "+VXha6g9Ycn+SbVbUQWcWfOBesh4MZ1Kg0XwwDnbpHC3FOE77sobQoSbBfO1MHeq5buuOxniFWmVOmJsJ7F6qgMNsvpzpfz6",
  "3DmucEOEiKXXVYn6YLFSIwVz2ce4cOuolo/7MuGrl4I78DxL7bwvCpFK5FpwjeBYortXClFhFZzZlWieEKt9+M9ybj67yep3",
  "rWBOdufa7UxnRPBUNffy2hNyj1leuzlT7DP2EuML9jltlJdLZyHYfdgA8ZjRXLmdZgrfjVJwdaZIWW/T279DdYL5ShPTHGIU",
  "PTM8PHwYF+qXPvxeXz3H41VpjtvK0/y+t8HgxIJ5d6YccXLqazxt5ua6WgreyIWZKcZTKTe88mwv2wpeaTQfUGUVPEzu4Cp/",
  "N5s8yKfhKjz2OsHzOAc37VPXCTGdc8wR4lLtCY8LUVa7OUuI5CrhMV10nScT5WCJ887ka4lP9g5vDpP/sIvw5Vr+B+tXne2b",
  "YrIR/Ck3Ub5JDtMHO0+X4w7dcnld95CCuTyT60bRlmlUlrXC50rBXKepBwsexA2AzPm+reA18mWirF0WwR5kPkWyh/U0c64T",
  "zOMm4oo9tZc1x1xLw7w31rqEwSd9RyzdLmJfE8EudM6a96X+1s0qUocFoovGclSM+Mpg4CruYagd0o/lXsBhBNN2aYKniS9P",
  "HDFNCmYPlTaCt2WK4AVEJ7gJbK9SbCs4TYgVMuf9toIvypfXZCW1CA6XKVz71/P/OWg+TWUDghfwQM6cY86NHzpipXg8RvS+",
  "V3jEiCy1xp8y513A3+Ir86a/FJzCHcVhy1GXrPILX0pKks3LXOGrcxTB7lwUSTRAnPQm2ad1kK47yOZy+kzZB78SNk6tpHvN",
  "DXe+VuttK1jvK1ZxUbmk2AoumGputGdbBX/DnYBzsOjhzr3863LUo9XqGhAcESzOyFVSrTaqnk99TLzy2Kua2eLSStn7bhVi",
  "kKziWq2RB80fmT+fi2yiu5bzYoZlgr12NcNjgh6rp3qon1PDIzJvBxCcxdf7Xu7AHjNRijipo8Lx0u18bhy9jK9Lreo0ad1q",
  "2e+6chWPJ8OrIjjMVrDc3l4i64/tIGtYcQlfEBeNdaPoD0u8VsiTZfuKuRWUfVH4xjcgWK6ZTHR33y8HUfXMhIVsMrxlfxJP",
  "lMQDwI2UGiJSnCq54mup6yoR4myeJq1QJ1N1mPtgbpy3642df4HlMzseRfvmqi3wNzHBPJUMIE2MnIXy7Kd37UIH17pl7rIl",
  "F48JmeEawYYQ86TZVnAPc9rSumnSY2pCSFe1JVDfzaKGBHvxUC8kpIHVRK15xfFLoXb0PjwtKuCqWcADquW1J25fOw/u6isy",
  "e98gWMcjjgI+eWakowgOnj5YFm0Jt6jifld1TKw/JndMd7WsZEVwoSwi9wNyJWG1nJLaCqYyXsX3/WqcreABSVzSKydTneD+",
  "A3lFYnyqXDgbIlc69o3RNCiYip+U///J+m9DnVHnTONqq6dO3joQA+SA2XhK6v0gwLpUubx2eHCNYFqvjsDGP0SOho+hX90d",
  "lgXzkip9bsgSlprkWk/P5e3Zr8T2PQsmk6dn3SBGCqao1Eon81uXEUmpPzHCyZ7Xb30jP7d+/keRtXfD3FKTRrj89BHa07kG",
  "JwJNRgq+hvzaqRSAYADBoKWpMHS9NsFoMGhRLAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAwC/Nb174",
  "1e3gpvjVP/7SivxCb1MU/6bVCH4BtprCC61GMCpw06pwqxEMV03jZyh6U32B/3xMGo3p2jiGEW4Q3BoFpykz6knVKs7OygPX",
  "JI0KhOAWFlyqhj9+oH+nLu19Gl3yj/gdTa9XcER5GATbleD0Yw/yX31Il0kHvmj0A4Fc3i7/UT4MwXDX7AvVGjK6uj0Um6YK",
  "5k2iwqtL7+a9mtSrlRDcwoLf79lTCp6WycF+9z/R2IKf96P7Iwn8mnc5a9auz8mkXM44dP6CpYlO3Tllys5RRJ8n7ojJguCW",
  "FezmPEcKruIo6jQms7EFP/IARcrONu+ED5XWjDApuzm699B8s2CXUH6kxWdx3pE1o8k7AYJbug9+9MHaja7ByxtZ7lE5/Urj",
  "4/gZjXkcep8SAkwKt88Rwz3Ngg1KtatrWY5hdwbvjIVgOxHscuCL5S6NLPcrimSJhvLkI6mm7DYp/JQLiv7OLPh0TkdJ8ufd",
  "OfEzCLYPwYaP51Y3ttg1MfKhOg8orpQnn080LNak8IM92+VUmgWnKSZ+4o6r7tvLvPNrCLYLwSW+H0Y0utj7qBWWNnWnvE0m",
  "end4vEnZYdQsXuJuFuztN4Y0i0Kd0nJGUKEfBNuF4Flzs5lGxt4+Mlh9CUjU5e2JXsLjZZNyKDrar9Iyivbf4ue3hB9xd2F4",
  "aPRiCLYLwV16Ss7erIC8d/XJ/Exdk5IdVWHTgTt5FavPJnFOu6WFSghuwbVoi2DzwrSSjZsNbVJwwjyz4DgtbhfejtuFN88/",
  "IOv2Nn3D/y+owm37Jzv40V0T9L7QivwCAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAJXfPvfeHW2E9/70L/i8we/v7mhD/O63",
  "MHodz93RpngORq/jvbYl+I8weh13tDFaT8nHR3oZb/4oHxMEtwrW3XM0Oif0oZs+rpfiBMG1OB+89OkcjZ0KfuuyM+nuq4mC",
  "4CYL1qxZ82jnB8fYqeDoXP4TsauMo/NcvcIx0GbfXfwZUXbR4Y1y7+wLuRyawys5/3BRCb8N+yy2Uib7Xy3rDcHWa71nV6KD",
  "Z+xU8KZ7is0brok7DtWMopF7EqfQqJ17Op5P5Rg80ZsT/OKpb8KWPZvezqdSv8C34o4QFQ3PGJwFwRY2/jeJfLr8x04FG/yU",
  "JZsfDiNNaF+iqzE00i+K0hd6ctizTew8ijQzulPfmrtJtzOXNmzWUHZOhX4h1/ojEGxl2n/Pfbwq3l5HWU6Vcy7n+EUVKr24",
  "qS6lkaeIchNdXV0PK/mLA/l1dyj1DeJ8zxQZh0/m96EXTr/NAwoDBFswzf1m4v6ze+1T74SX5N9Sv4kGRa8mjAwgmnxejVAZ",
  "tcNPvmygvjIUacKbPyiB8v27RXkk40lDcC1DzvKUcWxPZ7sU7K+o0d43HCpUenPMym9Vwd/FcRUNc9UslrH+o5Kp7xRVsHF4",
  "qgx+mP7QQg6VmArBFgZ+LLu6nvbZRkeETrmLNCMWjqKgt3zcp+xRBafvPEzGzUHkOTyNTFljLIIpY7sLVdfEt/vxXtJdhmAL",
  "5T3/QBFbP7bTibDXM8quoXFfa2jBM4lxWemqYEqNDo3bVEr05s688xk6q+D8exK3LPyIyPNtv7iJEGzltS9Czq6qtNdBlqYk",
  "stgcm7K00HoRRvhnq9thaaXXZF5gjlipS07HWrTtUpZnWgQRbjZgLRq3C1vPHX8YvY4/4YZ/2+ZfbeonO/+Ln+zQjT+6+2Ob",
  "+TnHc/ALAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAOrnxe+fvvMX5unv/4xybjG/f7uzGfj7iyjpFuL7O5uF51HSLcTTzSP4",
  "aZR0C3FnM4GSpvpjVbp5179bpyMXtwYPbucEwdQ6YlUGTb5xZ9TDRI9wnKwZlgRT1XWXwcI+9i14WjdHF2yNVVmfYM+dqmCv",
  "yroIlfpWJfiBs084umBrrMqgyf4XqmRbXFxUtJ5b7HJjrmffmnIdC47naIclS2P7UFis8vAEciqbVC1b5tLDH7nZt2Dvb7o4",
  "vGBrrMqgwLxDfoM5nOH5zXsSD3Ogs0O7FgUd3XOXuYlOTpx1JHHSxhnKhmyXwLzNWwbrKHLo4IyEGrsWfPDENIcXbIlVSUGb",
  "vKmPUnLXQg48e3qoSass1liaaBZ831tE1VPUJvpKgjeFhVZpEhYTVSn2LPj0E84QbIlVSUEc8Nik+JdHc/wk49HZWqWUbAQv",
  "rdntajT3wVndOWLljM8LldFE7kftWHB6cDU5vGBrrEp1kBWm+Bc9IxOGVmsVZ1vB9NCGxOgrqmC/TTJi5cQ0RQb1f9uOBW+9",
  "2Lnz8bOdezu0YGusSovg8jgeP5Uq2dcJTksn94eVfCk4cIgcno6OUiqI9PbcRF/55JNPtj34ySCHFmwTq9Is2BQXS5oDgaQK",
  "dq3xrhW8g0NF9xkeNloppIDQdNLGlWtO7HDSLFbsfB78qMP3wdZYlbWCqSwxNDrUyyx4QnRNiVlwr9DQrIVXKWLL8D7uR4bG",
  "DOUOW5uX6Ld5CQTbO3WxKi2TR/9iH8u2e741tbhSrnE45fMYLD9tnTo8q4jHWjSAYMLtQtwuJNzwxw1/O+fPf28Ov3/DT3Za",
  "jBef/+V/dPc8/AIAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAACAluXXzz51203w1D//ijJrVX5/f9tN8vtfo9RaEc/edtM8i1Jr",
  "RTx184KfQqm1Im5rAii1W0fn9lM5NCYfCG69dL+H6gJRRtSXY4LSyw4EOx9cs21cO/i6FcFeamCkG4goD7MDwS+PHxKw7z/w",
  "1RTBpjLj6UmzZSDKd53JaA5EWaktfjhVBjn0GuHuqiMqvLqUA3JULyAakczx0CqbW3BFTw539W7PHyCsCYL9a4I6zlC+ixqp",
  "bFjvEhizOZQDUWbsScw4Esj7g07JJjp155QpO0dRx8XkvjOBaEff5hY86Bj/Se2phbCmCFbeZGc71CZ6DgeibLekijJ2FVJl",
  "TiH9kOPFgl04hUbFeXMYvMolR9v5xFW2xCAr/dIyDYQ1SfAEoiGXVcFqIMqRb1EGRxrVbIml2Cw5yKpUql1dy3LStDn6Obuf",
  "qfaP82l+wZqxIWsL4atpgt2JAoJUwbvUQJTtKWMR7wsYTIFXpeB+R2VqR3/aUtaxbPepSYeaf5D1w6chk1B/b11wVoAcWI02",
  "C16QM7tGLwX3UXiCEsGDrcWLE03VCSNzm12wsVM3Z8i6RcHZSpQaiHJ0XLlZMAVt2aPOg112TSRN31AfSk1MoLCa8xOaXXC/",
  "B5OzmQgIuwXB3luGp1kCUZoFj1JSzQsd/lv8/Jb4E7ns/JZo8ODmnwe/1lOlAsJuBdtAlNfv6l3sRFiqBBBMuF2I24Vtkn/i",
  "hn/b5q/4yQ7hR3e27fOz8AsAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAABA6+b/AIKcDnOR",
  "XU+uAAAAAElFTkSuQmCC",
].join("");

export function chartPng(): Uint8Array {
  const bin = atob(CHART_PNG_BASE64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export const CANDIDATES_CSV = [
  "name,source,stage,permit",
  "Priya N.,LinkedIn,shortlisted,yes",
  "Tomás R.,LinkedIn,shortlisted,yes",
  "Lena K.,Referral,screening,check",
  "Omar S.,LinkedIn,screening,check",
].join("\n");

export const SHORTLIST_MD = [
  "# Backend shortlist (week 43)",
  "",
  "Screened 12 LinkedIn applicants against the must-haves.",
  "",
  "1. **Priya N.**: 6 yrs Go, payments; permit OK.",
  "2. **Tomás R.**: 8 yrs Kotlin; permit OK; salary above the band.",
  "3. Lena K.: strong, permit check pending.",
  "4. Omar S.: strong, permit check pending.",
].join("\n");
